import { computed, ref, watch } from 'vue'

import { useDocumentVisibility, useTimeoutFn } from '@vueuse/core'

import { badgeCount, badgeText, useMyWorkService, type MyWorkCounts } from '@/services/my-work'

import { usePollWhile } from './usePollWhile'

/** How often the side nav asks for the badge's counts while ADX is in front; a hidden tab asks nothing. */
export const BADGE_POLL_MS = 2 * 60 * 1000
/** The fleet reads a person's cached My work page again once it is this old, so counts from such a page are a step behind. */
export const BADGE_STALE_S = 5 * 60
/** How long after such an answer the badge asks once more, when the fleet's new read has had time to land. */
export const BADGE_FOLLOW_UP_MS = 30 * 1000

/**
 * The side nav's My work badge: PRs ready to merge plus decisions waiting on me, from the fleet's
 * cached page. A fleet without the counts route, or one that cannot be reached, shows no badge.
 */
export function useMyWorkBadge() {
  const isConfigured = Boolean(import.meta.env.VITE_FLEET_API_BASE_URL)
  const { useMyWorkCounts } = useMyWorkService()
  const { data: counts, refetch } = useMyWorkCounts({ enabled: isConfigured })

  // Counts read off an old page are a step behind, so the badge asks once more after the fresh read. The answer to
  // that ask never schedules another (a page the fleet cannot refresh waits for the next poll), and a tab hidden
  // meanwhile asks nothing: it asks on its return instead.
  const visibility = useDocumentVisibility()
  let followedUpAt = 0
  const followUp = useTimeoutFn(
    () => {
      if (visibility.value === 'hidden') return
      followedUpAt = Date.now()
      void refetch()
    },
    BADGE_FOLLOW_UP_MS,
    { immediate: false }
  )
  const afterAnswer = (value: MyWorkCounts | undefined) => {
    const justFollowedUp = Date.now() - followedUpAt < BADGE_FOLLOW_UP_MS
    if (!justFollowedUp && (value?.ageS ?? 0) >= BADGE_STALE_S) followUp.start()
  }
  // The poll reads each answer itself: an answer equal to the last one leaves `counts` as it was, so a watch alone
  // would miss it. The watch covers the first read and the ask on focus.
  usePollWhile(ref(isConfigured), async () => afterAnswer((await refetch())?.data), BADGE_POLL_MS)
  watch(counts, afterAnswer)

  const count = computed(() => badgeCount(counts.value))
  const text = computed(() => badgeText(count.value))

  return { count, text }
}
